/**
 * TotemEntity - Placed totem that attacks nearby enemies
 * Has HP, attack speed, range, and duration
 */

import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { ElementType, Vector2 } from '@shared/types';
import { CustomSkillV2, SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';
import { ELEMENT_INFO } from '@shared/skills/SkillDeliveryTypes';

export interface TotemEntityConfig {
  skill: CustomSkillV2;
  position: Vector2;
  ownerId: string;
  baseDamage: number;  // Player's base attack damage
  getDamage?: () => number; // Live damage callback for stat scaling
}

export interface TotemAttack {
  targetId: string;
  targetPos: Vector2;
  damage: number;
  element: ElementType;
}

export class TotemEntity {
  public readonly id: string;
  public position: Vector2;
  public ownerId: string;
  public isAlive: boolean = true;

  private maxHp: number;
  private hp: number;
  private attackSpeed: number;
  private attackRange: number;
  private damage: number;
  private damageMultiplier: number;
  private getDamageCallback: (() => number) | null;
  private element: ElementType;
  private color: string;
  private secondaryColor: string;
  private duration: number = 30; // Default duration in seconds
  private lifeTimer: number;

  // Attack state
  private attackCooldown: number = 0;
  private lastTargetId: string | null = null;
  private attackAnimation: number = 0;
  private pendingAttack: TotemAttack | null = null;

  // Totem type choice
  private totemType: 'projectile' | 'aoe_pulse' | 'beam' | 'taunt';

  // Visual
  private pulseTime: number = 0;
  private floatingOffset: number = 0;

  constructor(config: TotemEntityConfig) {
    this.id = `totem_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    this.position = { ...config.position };
    this.ownerId = config.ownerId;
    this.element = config.skill.element;

    // Calculate stats from skill
    this.maxHp = SkillCreatorV2.getTotemHealth(config.skill);
    this.hp = this.maxHp;
    this.attackSpeed = SkillCreatorV2.getAttackSpeed(config.skill);
    this.attackRange = SkillCreatorV2.getEffectiveRange(config.skill);
    this.damageMultiplier = 1.0; // V2 uses flat damage directly
    this.getDamageCallback = config.getDamage || null;
    // Totem damage comes from the skill's damage slider directly
    this.damage = SkillCreatorV2.getEffectiveDamage(config.skill);

    this.duration = SkillCreatorV2.getTotemDuration(config.skill);
    this.lifeTimer = this.duration;

    // Read totem type choice
    this.totemType = config.skill.totemType || 'projectile';

    // Taunt totems get 2x HP
    if (this.totemType === 'taunt') {
      this.maxHp *= 2;
      this.hp = this.maxHp;
    }

    // Get colors from element
    const elementInfo = ELEMENT_INFO[this.element];
    this.color = elementInfo.color;
    this.secondaryColor = this.getSecondaryColor(this.element);
  }

  private getSecondaryColor(element: ElementType): string {
    const colorMap: Record<ElementType, string> = {
      physical: '#a0a0a0',
      od: '#ffaa00',
      gok: '#00ccff',
      su: '#60c0ff',
      karanlik: '#c080ff',
    };
    return colorMap[element];
  }

  getHp(): number {
    return this.hp;
  }

  getMaxHp(): number {
    return this.maxHp;
  }

  getHealthPercent(): number {
    return this.maxHp > 0 ? this.hp / this.maxHp : 0;
  }

  getDamage(): number {
    return this.damage;
  }

  getElement(): ElementType {
    return this.element;
  }

  getAttackRange(): number {
    return this.attackRange;
  }

  getTotemType(): 'projectile' | 'aoe_pulse' | 'beam' | 'taunt' {
    return this.totemType;
  }

  getRemainingTime(): number {
    return this.lifeTimer;
  }

  /**
   * Take damage
   */
  takeDamage(amount: number): number {
    const actualDamage = Math.min(this.hp, amount);
    this.hp -= actualDamage;

    if (this.hp <= 0) {
      this.hp = 0;
      this.isAlive = false;
    }

    return actualDamage;
  }

  /**
   * Check if entity is in attack range and can be targeted
   */
  isInRange(entityX: number, entityY: number): boolean {
    const dx = entityX - this.position.x;
    const dy = entityY - this.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance <= this.attackRange;
  }

  /**
   * Try to attack a target. Returns attack info if successful.
   */
  tryAttack(targetId: string, targetX: number, targetY: number): TotemAttack | null {
    if (this.attackCooldown > 0) return null;
    if (!this.isInRange(targetX, targetY)) return null;

    // Calculate cooldown based on attack speed
    const baseCooldown = 1.5;
    this.attackCooldown = baseCooldown / this.attackSpeed;
    this.lastTargetId = targetId;
    this.attackAnimation = 0.3; // Attack animation duration

    // Recalculate damage from live player stats if callback available
    const currentDamage = this.getDamageCallback
      ? this.getDamageCallback() * this.damageMultiplier
      : this.damage;

    const attack: TotemAttack = {
      targetId,
      targetPos: { x: targetX, y: targetY },
      damage: Math.floor(currentDamage),
      element: this.element,
    };

    this.pendingAttack = attack;
    return attack;
  }

  /**
   * Get and clear pending attack (for GameWorld to process)
   */
  getPendingAttack(): TotemAttack | null {
    const attack = this.pendingAttack;
    this.pendingAttack = null;
    return attack;
  }

  update(deltaTime: number): void {
    this.pulseTime += deltaTime;
    this.floatingOffset = Math.sin(this.pulseTime * 2) * 3;

    // Decrease life timer
    this.lifeTimer -= deltaTime;
    if (this.lifeTimer <= 0) {
      this.isAlive = false;
    }

    // Update attack cooldown
    if (this.attackCooldown > 0) {
      this.attackCooldown -= deltaTime;
    }

    // Update attack animation
    if (this.attackAnimation > 0) {
      this.attackAnimation -= deltaTime;
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive) return;

    const ctx = renderer.context;
    const screenPos = camera.worldToScreen(this.position.x, this.position.y);
    const z = camera.zoom;

    ctx.save();

    const baseY = screenPos.y + this.floatingOffset * z;

    // Shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 25 * z, 18 * z, 8 * z, 0, 0, Math.PI * 2);
    ctx.fill();

    // Totem base
    this.renderTotemBody(ctx, screenPos.x, baseY, z);

    // Attack effect
    if (this.attackAnimation > 0 && this.lastTargetId) {
      this.renderAttackEffect(ctx, screenPos.x, baseY, z);
    }

    // Health bar
    this.renderHealthBar(ctx, screenPos.x, baseY - 45 * z, z);

    // Duration indicator
    this.renderDurationBar(ctx, screenPos.x, baseY - 55 * z, z);

    // Range indicator (faint)
    ctx.strokeStyle = this.color + '20';
    ctx.lineWidth = 1;
    ctx.setLineDash([4 * z, 4 * z]);
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y, this.attackRange * z, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.restore();
  }

  private renderTotemBody(ctx: CanvasRenderingContext2D, x: number, y: number, z: number): void {
    const pulse = Math.sin(this.pulseTime * 3) * 0.1 + 1;
    const attackPulse = this.attackAnimation > 0 ? 1.2 : 1;

    // Totem pillar
    const pillarWidth = 16 * z;
    const pillarHeight = 50 * z;

    // Base stone
    ctx.fillStyle = '#3a3228';
    ctx.beginPath();
    ctx.moveTo(x - pillarWidth, y + 10 * z);
    ctx.lineTo(x + pillarWidth, y + 10 * z);
    ctx.lineTo(x + pillarWidth * 0.8, y - pillarHeight * 0.3);
    ctx.lineTo(x - pillarWidth * 0.8, y - pillarHeight * 0.3);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#2a2218';
    ctx.lineWidth = 2 * z;
    ctx.stroke();

    // Upper pillar
    ctx.fillStyle = '#4a4238';
    ctx.beginPath();
    ctx.moveTo(x - pillarWidth * 0.7, y - pillarHeight * 0.3);
    ctx.lineTo(x + pillarWidth * 0.7, y - pillarHeight * 0.3);
    ctx.lineTo(x + pillarWidth * 0.5, y - pillarHeight * 0.8);
    ctx.lineTo(x - pillarWidth * 0.5, y - pillarHeight * 0.8);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Elemental orb at top
    const orbY = y - pillarHeight * 0.9;
    const orbSize = 12 * z * pulse * attackPulse;

    // Orb glow
    const orbGlow = ctx.createRadialGradient(x, orbY, 0, x, orbY, orbSize * 2);
    orbGlow.addColorStop(0, this.color + '80');
    orbGlow.addColorStop(0.5, this.color + '40');
    orbGlow.addColorStop(1, 'transparent');
    ctx.fillStyle = orbGlow;
    ctx.beginPath();
    ctx.arc(x, orbY, orbSize * 2, 0, Math.PI * 2);
    ctx.fill();

    // Orb core
    const orbCore = ctx.createRadialGradient(x - 3 * z, orbY - 3 * z, 0, x, orbY, orbSize);
    orbCore.addColorStop(0, '#ffffff');
    orbCore.addColorStop(0.3, this.secondaryColor);
    orbCore.addColorStop(1, this.color);
    ctx.fillStyle = orbCore;
    ctx.beginPath();
    ctx.arc(x, orbY, orbSize, 0, Math.PI * 2);
    ctx.fill();

    // Elemental runes on pillar
    const runePositions = [0.2, 0.5, 0.7];
    ctx.fillStyle = this.color + '60';
    ctx.font = `${8 * z}px Arial`;
    ctx.textAlign = 'center';
    for (const pos of runePositions) {
      const runeY = y + 10 * z - pillarHeight * pos;
      ctx.fillText(ELEMENT_INFO[this.element].icon, x, runeY + 3 * z);
    }

    // Energy particles
    const particleCount = 4;
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2 + this.pulseTime * 2;
      const dist = orbSize * 1.5;
      const px = x + Math.cos(angle) * dist;
      const py = orbY + Math.sin(angle) * dist * 0.5;
      const size = 2 * z;

      ctx.fillStyle = this.secondaryColor;
      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderAttackEffect(ctx: CanvasRenderingContext2D, x: number, y: number, z: number): void {
    const progress = 1 - (this.attackAnimation / 0.3);
    const orbY = y - 50 * z * 0.9;

    // Energy burst from orb (expanding ring)
    const burstSize = 20 * z * progress;
    const burstAlpha = Math.floor((1 - progress) * 150);
    ctx.strokeStyle = this.color + burstAlpha.toString(16).padStart(2, '0');
    ctx.lineWidth = (3 - progress * 2) * z;
    ctx.beginPath();
    ctx.arc(x, orbY, burstSize, 0, Math.PI * 2);
    ctx.stroke();

    // Element-colored projectile ball traveling toward target
    if (this.pendingAttack) {
      const tx = this.pendingAttack.targetPos.x;
      const ty = this.pendingAttack.targetPos.y;
      // Approximate target direction from totem
      const dx = tx - this.position.x;
      const dy = ty - this.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const ndx = dx / dist;
      const ndy = dy / dist;

      // Projectile position along path (screen space offset from orb)
      const travelDist = Math.min(dist, 150) * z * progress;
      const projX = x + ndx * travelDist;
      const projY = orbY + ndy * travelDist;
      const projAlpha = Math.max(0, 1 - progress * 0.5);
      const projSize = 5 * z;

      // Trail (3 fading segments behind the projectile)
      for (let i = 3; i >= 1; i--) {
        const trailProgress = Math.max(0, progress - i * 0.06);
        const trailDist = Math.min(dist, 150) * z * trailProgress;
        const trailX = x + ndx * trailDist;
        const trailY = orbY + ndy * trailDist;
        const trailAlpha = Math.floor(projAlpha * (1 - i * 0.25) * 120);
        if (trailAlpha > 0) {
          ctx.fillStyle = this.secondaryColor + trailAlpha.toString(16).padStart(2, '0');
          ctx.beginPath();
          ctx.arc(trailX, trailY, projSize * (1 - i * 0.15), 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Main projectile ball with glow
      ctx.save();
      ctx.shadowColor = this.color;
      ctx.shadowBlur = 12 * z;
      const projGrad = ctx.createRadialGradient(projX, projY, 0, projX, projY, projSize);
      projGrad.addColorStop(0, '#ffffff');
      projGrad.addColorStop(0.4, this.color);
      projGrad.addColorStop(1, this.color + '00');
      ctx.fillStyle = projGrad;
      ctx.globalAlpha = projAlpha;
      ctx.beginPath();
      ctx.arc(projX, projY, projSize * 1.5, 0, Math.PI * 2);
      ctx.fill();

      // Solid core
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(projX, projY, projSize * 0.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      ctx.restore();
    }
  }

  private renderHealthBar(ctx: CanvasRenderingContext2D, x: number, y: number, z: number): void {
    const barWidth = 30 * z;
    const barHeight = 4 * z;
    const healthPercent = this.maxHp > 0 ? this.hp / this.maxHp : 0;

    // Background
    ctx.fillStyle = '#000000';
    ctx.fillRect(x - barWidth / 2, y, barWidth, barHeight);

    // Health fill
    const healthColor = healthPercent > 0.5 ? '#2ecc71' : healthPercent > 0.25 ? '#f39c12' : '#e74c3c';
    ctx.fillStyle = healthColor;
    ctx.fillRect(x - barWidth / 2, y, barWidth * healthPercent, barHeight);

    // Border
    ctx.strokeStyle = '#ffffff40';
    ctx.lineWidth = 1;
    ctx.strokeRect(x - barWidth / 2, y, barWidth, barHeight);
  }

  private renderDurationBar(ctx: CanvasRenderingContext2D, x: number, y: number, z: number): void {
    const barWidth = 30 * z;
    const barHeight = 2 * z;
    const durationPercent = this.lifeTimer / this.duration;

    // Background
    ctx.fillStyle = '#00000060';
    ctx.fillRect(x - barWidth / 2, y, barWidth, barHeight);

    // Duration fill
    ctx.fillStyle = this.color + '80';
    ctx.fillRect(x - barWidth / 2, y, barWidth * durationPercent, barHeight);
  }
}

/**
 * Factory function to create totem entity
 */
export function createTotemEntity(config: TotemEntityConfig): TotemEntity {
  return new TotemEntity(config);
}
